import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Download, Mail, X, AlertTriangle } from "lucide-react";
import { http, fmtErr } from "../lib/api";

const DEFAULT_SUBJECT = "We're LIVE — order your chamoy candy at alekastreats.com";

const DEFAULT_HTML = `<div style="font-family:Georgia,serif;max-width:560px;margin:0 auto;color:#1A1A1A;">
  <h1 style="font-family:'Anton',Impact,sans-serif;letter-spacing:2px;color:#D32F2F;text-transform:uppercase;font-size:36px;margin:0 0 8px;">Online orders are OPEN</h1>
  <p style="font-size:16px;line-height:1.6;">Hey {{name}},</p>
  <p style="font-size:16px;line-height:1.6;">You joined the waitlist for real-deal chamoy candy made with <b>100% Mexican spices</b> — and today's the day. We're officially LIVE at <a href="https://alekastreats.com" style="color:#D32F2F;font-weight:bold;">alekastreats.com</a>.</p>
  <p style="text-align:center;margin:32px 0;">
    <a href="https://alekastreats.com/shop" style="background:#D32F2F;color:#fff;padding:16px 32px;text-decoration:none;font-family:'Anton',sans-serif;letter-spacing:2px;text-transform:uppercase;font-size:14px;">Shop the drop &rarr;</a>
  </p>
  <p style="font-size:16px;line-height:1.6;">As a thank-you for being early — here's your personal share link if you want to send a friend:</p>
  <p style="background:#FDF4E3;padding:12px;border:1px solid #E5D8C1;font-family:monospace;font-size:14px;text-align:center;">{{share_url}}</p>
  <p style="font-size:14px;line-height:1.6;color:#5C5A56;">Made with love in Monterrey · shipped from the US · Buy 3, Get 1 FREE on retail this week only.</p>
  <p style="font-size:14px;color:#5C5A56;">— Alekas Treats LLC</p>
</div>`;

export function LaunchBlastModal({ waitlistCount, onClose }) {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [dryCount, setDryCount] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const dryRun = async () => {
    setRunning(true);
    try {
      const { data } = await http.post("/admin/waitlist/blast", { subject, html, dry_run: true });
      setDryCount(data.recipients_count);
      toast.success(`Dry run: ${data.recipients_count} unique recipients — nothing was sent.`);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Failed"); }
    finally { setRunning(false); }
  };

  const sendReal = async () => {
    const c = dryCount ?? waitlistCount;
    if (!window.confirm(`Send this blast to ${c} recipients RIGHT NOW? This is not reversible.`)) return;
    if (!window.confirm(`Are you 100% sure? Type OK on the next prompt to confirm.`)) return;
    const typed = window.prompt("Type SEND to confirm:");
    if (typed !== "SEND") { toast.error("Cancelled — you didn't type SEND."); return; }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await http.post("/admin/waitlist/blast", { subject, html, dry_run: false });
      setResult(data);
      toast.success(`Blast complete — sent ${data.sent} · failed ${data.failed.length}`);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Failed"); }
    finally { setRunning(false); }
  };

  const preview = html
    .replace(/{{name}}/g, "friend")
    .replace(/{{email}}/g, "friend@example.com")
    .replace(/{{referral_code}}/g, "ALK-DEMO1")
    .replace(/{{share_url}}/g, "https://alekastreats.com/?ref=ALK-DEMO1");

  return (
    <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4 overflow-y-auto" data-testid="blast-modal">
      <div className="bg-white max-w-5xl w-full my-8 border-2 border-[#1A1A1A]">
        <div className="flex items-center justify-between border-b-2 border-[#1A1A1A] px-6 py-4">
          <div className="flex items-center gap-2">
            <Mail size={20} className="text-[#D32F2F]" />
            <div>
              <div className="font-heading text-2xl uppercase tracking-wide">Launch Blast</div>
              <div className="text-xs text-[#5C5A56]">{waitlistCount} on the list · placeholders: <code>{'{{name}}'}</code> <code>{'{{email}}'}</code> <code>{'{{referral_code}}'}</code> <code>{'{{share_url}}'}</code></div>
            </div>
          </div>
          <button onClick={onClose} className="text-[#5C5A56] hover:text-[#D32F2F]" data-testid="blast-close"><X size={20} /></button>
        </div>

        <div className="grid md:grid-cols-2 gap-0 max-h-[70vh] overflow-y-auto">
          <div className="p-6 space-y-3 border-r border-[#E5D8C1]">
            <label className="block text-xs uppercase tracking-widest text-[#5C5A56]">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-white border border-[#E5D8C1] px-3 py-2" data-testid="blast-subject" />
            <label className="block text-xs uppercase tracking-widest text-[#5C5A56] mt-3">HTML body</label>
            <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={16} className="w-full bg-white border border-[#E5D8C1] px-3 py-2 font-mono text-xs" data-testid="blast-html" />
          </div>
          <div className="p-6 bg-[#FDF4E3]">
            <div className="text-xs uppercase tracking-widest text-[#5C5A56] mb-2">Live preview (placeholders → demo values)</div>
            <div className="bg-white border border-[#E5D8C1] p-4 min-h-[400px]" dangerouslySetInnerHTML={{ __html: preview }} data-testid="blast-preview" />
          </div>
        </div>

        {result && (
          <div className="px-6 py-3 border-t border-[#E5D8C1] bg-[#7CB342]/10">
            <div className="font-heading text-sm uppercase tracking-widest">Result</div>
            <div className="text-sm">Sent: <b>{result.sent}</b> · Failed: <b>{result.failed.length}</b> of {result.recipients_count}</div>
            {result.failed.length > 0 && (
              <details className="text-xs mt-1 text-[#5C5A56]">
                <summary className="cursor-pointer">Show failures</summary>
                <ul className="mt-1 space-y-0.5 max-h-24 overflow-y-auto">
                  {result.failed.map((f, i) => <li key={i}><b>{f.email}</b>: {f.error}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="flex items-center justify-between px-6 py-4 border-t-2 border-[#1A1A1A] bg-white">
          <div className="text-xs text-[#5C5A56] flex items-center gap-2">
            <AlertTriangle size={14} className="text-[#FFB300]" />
            Real emails go out on send. Always dry-run first.
          </div>
          <div className="flex gap-2">
            <button
              onClick={dryRun}
              disabled={running}
              className="border border-[#1A1A1A] px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#1A1A1A] hover:text-white disabled:opacity-50 flex items-center gap-1"
              data-testid="blast-dry-run"
            >
              {running && <Loader2 className="animate-spin" size={12} />} Dry run
            </button>
            <button
              onClick={sendReal}
              disabled={running || dryCount === null}
              title={dryCount === null ? "Do a dry run first" : "Send for real"}
              className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-40 flex items-center gap-1"
              data-testid="blast-send"
            >
              {running && <Loader2 className="animate-spin" size={12} />} <Send size={12} /> Send to {dryCount ?? "?"} recipients
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function WaitlistToolbar({ waitlistCount }) {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await http.get("/admin/waitlist/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "alekas-waitlist.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Waitlist CSV downloaded");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <>
      <div className="flex gap-2">
        <button
          onClick={exportCsv}
          disabled={exporting || waitlistCount === 0}
          className="border border-[#1A1A1A] px-3 py-2 text-xs uppercase tracking-widest hover:bg-[#1A1A1A] hover:text-white disabled:opacity-40 flex items-center gap-1"
          data-testid="waitlist-export-csv"
        >
          {exporting ? <Loader2 className="animate-spin" size={12} /> : <Download size={12} />} Export CSV
        </button>
        <button
          onClick={() => setOpen(true)}
          disabled={waitlistCount === 0}
          className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white px-3 py-2 text-xs uppercase tracking-widest disabled:opacity-40 flex items-center gap-1"
          data-testid="waitlist-blast-open"
        >
          <Mail size={12} /> Send launch blast
        </button>
      </div>
      {open && <LaunchBlastModal waitlistCount={waitlistCount} onClose={() => setOpen(false)} />}
    </>
  );
}
